/* eslint-disable react-hooks/exhaustive-deps */
import { LightBulbIcon } from "@heroicons/react/20/solid";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import BasicSelect from "../inputs/select/BasicSelect";

type Props = {
  list?: any[];
  onDone?: any;
  selectSort: any;
  selectSortDir: any;
  selectedColumns?: any[];
  children?: React.ReactNode;
  isOpen: any;
  setIsOpen: any;
};

const SortDropdown = ({
  list = [],
  isOpen = false,
  onDone,
  selectedColumns = [],
  setIsOpen,
  selectSortDir,
  children,
  selectSort,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [sort, setSort] = useState(selectSort);
  const [sortDir, setSortDir] = useState(selectSortDir);

  useEffect(() => {
    setSort(selectSort);
    setSortDir(selectSortDir);
  }, [selectSort, selectSortDir]);

  const displayTraits: any = useMemo(
    () => list
      ?.filter((item: any) => selectedColumns.includes(item.name))
      .map((item: any) => ({
        id: item?.name || item?.display_name,
        name: item?.display_name,
        type: item?.data_type,
        entityType: item?.attribute_type,
        value: item?.name,
      })),
    [list, selectedColumns],
  );

  const sortOrder: any = useMemo(() => {
    if (sort?.entityType === "metric_traits") {
      return [
        { name: "Number(1 → 9)", order: "asc", id: 1, type: "changeval" },
        { name: "Number(9 → 1)", order: "desc", id: 2, type: "changeval" },
        { name: "Change %(1 → 100)", order: "asc", id: 3, type: "growthval" },
        { name: "Change %(100 → 1)", order: "desc", id: 4, type: "growthval" },
      ];
    }

    if (sort?.type === "string") {
      return [
        { name: "Text(A → Z)", order: "asc", id: 1, type: "property" },
        { name: "Text(Z → A)", order: "desc", id: 2, type: "property" },
      ];
    }

    if (sort?.type === "datetime") {
      return [
        { name: "Date(New → Old)", order: "desc", id: 1, type: "property" },
        { name: "Date(Old → New)", order: "asc", id: 2, type: "property" },
      ];
    }

    if (sort?.type === "int" || sort?.type === "float") {
      return [
        { name: "Number(1 → 9)", order: "asc", id: 1, type: "property" },
        { name: "Number(9 → 1)", order: "desc", id: 2, type: "property" },
      ];
    }

    return [
      { name: "Ascending", order: "asc", id: 1, type: "property" },
      { name: "Descending", order: "desc", id: 2, type: "property" },
    ];
  }, [sort]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div ref={ref} className="inline-block">
          {children}
        </div>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" className="w-[430px] p-3">
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-amber-900">
          <LightBulbIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <p className="text-xs font-medium">
            Not seeing what you&apos;re looking for? Try adding columns to the table.
          </p>
        </div>

        <div className="flex items-start justify-between gap-2">
          <BasicSelect
            containerClass="w-full"
            label="Sort By"
            selectedItem={displayTraits.find((item: any) => item.value === sort?.value)}
            list={displayTraits}
            setSelectedItem={setSort}
          />
          <BasicSelect
            containerClass="w-[80%]"
            label="Sort Order"
            list={sortOrder}
            selectedItem={sortOrder.find((item: any) => item.order === sortDir?.order && item.type === sortDir?.type) || sortOrder[0]}
            setSelectedItem={setSortDir}
          />
        </div>

        <div className="mt-3 flex justify-end gap-1 border-t border-border pt-2">
          <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>Close</Button>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              onDone(sort, sortDir);
              setIsOpen(false);
            }}
          >
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SortDropdown;
